import { } from '../unit-meta/batch.js';
import {
  CertificationScheme,
  CertificationSchemeOther,
} from '../unit-meta/certification.js';
import {
  feedstockDatabase,
  type FeedstockName,
} from '../unit-meta/feedstock-db.js';
import { type TotalLCAIncludeEmissions } from '../unit-meta/greenhouseGas.js';
import { BaselineCarbonIntensity } from '../unit-meta/product.js';
import {
  ConversionProcess,
  RegulatoryScheme,
} from '../unit-meta/shared.js';

export const tier1Schemes: RegulatoryScheme[] = [
  RegulatoryScheme.France,
  RegulatoryScheme.Norway,
  RegulatoryScheme.RefuelEU,
  RegulatoryScheme.Netherlands,
  RegulatoryScheme.SouthKorea,
  RegulatoryScheme.Brazil,
  RegulatoryScheme.Canadian,
  RegulatoryScheme.UK,
];

export const tierASchemes = [CertificationScheme.RSBGlobal, CertificationScheme.ISCCPLUS];

export const getValue = (value: number | undefined, defaultValue: number = 0) =>
  value ?? defaultValue;

export const getActualGHGIntensityAirportDelivery = (
  greenhouseGas: TotalLCAIncludeEmissions,
) => {
  if (greenhouseGas.totalLCAValueincludeEmissionsFromDeliveryToAirport) {
    return 0;
  }

  if (greenhouseGas.useDefaultValueForDeliveryToAirport) {
    return 0.9;
  }

  return getValue(
    'actualGHGIntensityAirportDelivery' in greenhouseGas
      ? greenhouseGas.actualGHGIntensityAirportDelivery
      : 0,
  );
};

export enum SustainabilityTierTypes {
  C = 'SCS-eligible',
  B = 'SABA-eligible',
  A = 'SABA-advanced',
}

export const UsabilityTierInfoForSAFC = {
  1: 'valid for compliance reporting',
  2: 'voluntary units, no SAFcE issued',
  3: 'voluntary units, SAFcE has been issued',
};

export const CertificationSchemeToLabel: Record<
  CertificationScheme | CertificationSchemeOther,
  string
> = {
  [CertificationScheme.RSBCORSIA]: 'RSB CORSIA',
  [CertificationScheme.ISCCCORSIA]: 'ISCC CORSIA',
  [CertificationScheme.CLASSNKCORSIA]: 'ClassNK CORSIA',
  [CertificationScheme.RSBEURED]: 'RSB EU RED',
  [CertificationScheme.ISCCEU]: 'ISCC EU',
  [CertificationScheme.RSBGlobal]: 'RSB Global',
  [CertificationScheme.ISCCPLUS]: 'ISCC PLUS',
  [CertificationSchemeOther]: 'Other',
};

export const JetFuelBaselineCarbonIntensityToLabel: Record<
  BaselineCarbonIntensity,
  string
> = {
  [BaselineCarbonIntensity.GCORSIAJetFuel]: '89g (CORSIA jet fuel)',
  [BaselineCarbonIntensity.GEURED]: '94g (EU RED)',
  [BaselineCarbonIntensity.GRSBGlobal]: '90g (RSB Global)',
  [BaselineCarbonIntensity.GCORSIAAviationGasoline]: '95g (CORSIA aviation gasoline)',
};

export const conversionProcessToLabel: Record<ConversionProcess, string>
  = {
    [ConversionProcess.HEFA]: 'HEFA (Hydroprocessed Esters and Fatty Esters)',
    [ConversionProcess.ATJIntegrated]: 'ATJ-Ethanol - integrated conversion design',
    [ConversionProcess.ATJStandalone]: 'ATJ-Ethanol - standalone conversion design',
    [ConversionProcess.ATJIsobutanol]: 'ATJ-Isobutanol',
    [ConversionProcess.FT]: 'Gasification FT (Fischer-Tropsch)',
    [ConversionProcess.HEFACoProcessed]: 'HEFA co-processed at petroleum refinery',
    [ConversionProcess.SIP]: 'Synthesized iso-paraffins (SIP)',
    [ConversionProcess.PTL]: 'PtL (power to liquids, including e-fuels, RFNBOs)',
  };

export const tierCFeedStock: string[] = [
  'palmOil',
  'palmFattyAcidDistillate',
  'palmFattyAcidDistillate',
  'palmOilMillEffluent',
  'palmKernelMeal',
] satisfies FeedstockName[];

export const FeedstockNameToLabel = Object.fromEntries(feedstockDatabase.map(f => [f.name, f.label]));
